import { useRef, useState } from 'react';
import type { TextLocationBox } from '@avisoma/shared';
import backend from '../../services/backend';
import { collectLocateTexts } from './collectLocateTexts';

export type FieldHighlight = {
  pageWidth: number;
  pageHeight: number;
  boxes: TextLocationBox[];
};

type Cache = {
  pageWidth: number;
  pageHeight: number;
  byText: Map<string, TextLocationBox[]>;
};

export function usePageTextLocations(
  pageId: string,
  variant: 'rotated' | 'original',
  extractionResult: unknown,
) {
  const [highlight, setHighlight] = useState<FieldHighlight | null>(null);
  const cacheRef = useRef<Cache | null>(null);
  const inflightRef = useRef<Promise<Cache | null> | null>(null);
  const lastTextRef = useRef<string | null>(null);
  const texts = collectLocateTexts(extractionResult).slice(0, 500);
  const cacheKey = `${pageId}:${variant}:${texts.join('\u0001')}`;
  const keyRef = useRef(cacheKey);
  if (keyRef.current !== cacheKey) {
    keyRef.current = cacheKey;
    cacheRef.current = null;
    inflightRef.current = null;
    lastTextRef.current = null;
    if (highlight) setHighlight(null);
  }

  const loadCache = (): Promise<Cache | null> => {
    if (cacheRef.current) return Promise.resolve(cacheRef.current);
    if (inflightRef.current) return inflightRef.current;
    if (texts.length === 0) return Promise.resolve(null);
    const requestKey = cacheKey;
    const request = backend
      .locateDocumentPageText(
        pageId,
        { queries: texts.map((text) => ({ id: text, text })) },
        { variant },
      )
      .then((res) => {
        if (keyRef.current !== requestKey) return null;
        const cache: Cache = {
          pageWidth: res.data.pageWidth,
          pageHeight: res.data.pageHeight,
          byText: new Map(res.data.results.map((row: { text: string; boxes: TextLocationBox[] }) => [row.text, row.boxes])),
        };
        cacheRef.current = cache;
        inflightRef.current = null;
        return cache;
      })
      .catch((err) => {
        console.error('text-locations failed', err);
        inflightRef.current = null;
        return null;
      });
    inflightRef.current = request;
    return request;
  };

  const hoverText = (text: string | null) => {
    if (lastTextRef.current === text) return;
    lastTextRef.current = text;
    if (!text) {
      setHighlight(null);
      return;
    }
    void loadCache().then((cache) => {
      if (!cache || lastTextRef.current !== text) return;
      const boxes = cache.byText.get(text) ?? [];
      setHighlight({ pageWidth: cache.pageWidth, pageHeight: cache.pageHeight, boxes });
    });
  };

  return { highlight, hoverText };
}
